import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { NavigationContainer, useNavigationContainerRef, DarkTheme } from '@react-navigation/native';

import { DesktopHeader } from '@/components/residents/desktop/DesktopHeader';
import { DesktopSidebar, DesktopSidebarItem } from '@/components/residents/desktop/DesktopSidebar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';

import { PaymentsScreen } from '@/screens/residents/payments/PaymentsScreen';
// import { PaymentWizardScreen } from '@/screens/residents/payments/PaymentWizardScreen';
import TransactionProcessingScreen from '@/screens/residents/payments/TransactionProcessingScreen';
import TransactionReceivedScreen from '@/screens/residents/payments/TransactionReceivedScreen';
import TransactionTimeoutScreen from '@/screens/residents/payments/TransactionTimeoutScreen';
import AutoPayWizardScreen from '@/screens/residents/payments/AutoPayWizardScreen';
import AddOrChangePaymentMethodModal from '@/screens/residents/payments/AddOrChangePaymentMethodModal';
import ManageMyWalletModal from '@/screens/residents/payments/ManageMyWalletModal';
// TODO: REVISIT FEATURES
import { MaintenanceScreen } from '@/screens/residents/maintenance/MaintenanceScreen';
import { DesktopBulletinsScreen } from '@/screens/residents/bulletins/desktop/DesktopBulletinsScreen';
import DesktopAccountScreen from '@/screens/residents/account/desktop/DesktopAccountScreen';
import { LinkedAccountsScreen } from '@/screens/residents/linkedAccounts/LinkedAccountsScreen';
import { DesktopCommunityResourcesView } from '@/screens/residents/resources/desktop/DesktopCommunityResourcesView';
import { DesktopFAQScreen } from '@/screens/residents/faq/DesktopFAQScreen';
import PaymentSummaryModal from '@/screens/residents/payments/PaymentSummaryModal';
import ConfirmPaymentDetailsModal from '@/screens/residents/payments/ConfirmPaymentDetailsModal';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { NavigationGroup } from '@/constants/residents/residentNavigation';
import { DesktopEventsScreen } from '@/screens/residents/events/desktop/DesktopEventsScreen';
import { MessagesScreen } from '@/screens/residents/messages/MessagesScreen';
import { CommunityMarketplaceScreen } from '@/screens/residents/marketplace/CommunityMarketplaceScreen';
import { MarketplaceProfileScreen } from '@/screens/residents/marketplace/MarketplaceProfileScreen';
import { MarketplaceItemDetailScreen } from '@/screens/residents/marketplace/MarketplaceItemDetailScreen';
import { SellerProfileScreen } from '@/screens/residents/marketplace/SellerProfileScreen';
import { MyListingItemDetailScreen } from '@/screens/residents/marketplace/MyListingItemDetailScreen';
import { SavedListingsScreen } from '@/screens/residents/marketplace/SavedListingsScreen';
import { SavedItemDetailScreen } from '@/screens/residents/marketplace/SavedItemDetailScreen';
import { MarketplaceInboxScreen } from '@/screens/residents/marketplace/MarketplaceInboxScreen';
import { InboxConversationScreen } from '@/screens/residents/marketplace/InboxConversationScreen';
import { MarketplaceFollowingScreen } from '@/screens/residents/marketplace/MarketplaceFollowingScreen';
import { MarketplaceListing, MyListing, SavedListing } from '@/types/residents/marketplaceListing';
import { HomeScreen } from '@/screens/residents/home/HomeScreen';
import { TransactionHistoryScreen } from '@/screens/residents/payments/TransactionHistoryScreen';
import { CommunityBoardScreen } from '@/screens/residents/corkBoard/CommunityBoardScreen';
import { SurveysListScreen } from '@/screens/residents/corkBoard/SurveysListScreen';
import { ThreadDetailScreen } from '@/screens/residents/corkBoard/ThreadDetailScreen';
import { CreateThreadScreen } from '@/screens/residents/corkBoard/CreateThreadScreen';
import { EditThreadScreen } from '@/screens/residents/corkBoard/EditThreadScreen';
import { MyThreadsScreen } from '@/screens/residents/corkBoard/MyThreadsScreen';
import { SavedThreadsScreen } from '@/screens/residents/corkBoard/SavedThreadsScreen';
import { PlannerScreen } from '@/screens/residents/planner/PlannerScreen';

export type DesktopMainParamList = {
  Home: undefined;
  Planner: undefined;
  Payments: undefined;
  PaymentWizard: { 
    leaseId: number; 
    residentId?: number; 
    leaseUser?: any;
    amount?: number;
    payFullAmount?: boolean;
    paymentMethodId?: string | number;
    confirm?: boolean;
    managePaymentMethods?: boolean;
  } | undefined;
  PaymentSummaryModal: { leaseId: number; residentId?: number } | undefined;
  ConfirmPaymentDetails: { 
    leaseId: number; 
    amount: number; 
    payFullAmount: boolean; 
    paymentMethod?: { id?: string | number; brand?: string; last4?: string; type?: string } 
  } | undefined;
  TransactionProcessing: { 
    leaseId: number; 
    residentId?: number; 
    amount?: number; 
    sourceId?: number;
    onSuccess?: string;
    onFailure?: string;
    onTimeout?: string;
  } | undefined;
  TransactionReceived: { 
    leaseId: number; 
    residentId?: number; 
    amount?: number; 
    paymentMethod?: any; 
    calculatedFees?: any;
    onBehalfOfName?: string;
  } | undefined;
  TransactionTimeout: { 
    leaseId: number; 
    residentId?: number 
  } | undefined;
  AutoPayWizard: { 
    leaseId: number; 
    residentId?: number;
    fromTransactionReceived?: boolean;
  } | undefined;
  AddOrChangePaymentMethod: { 
    leaseId: number; 
    residentId?: number; 
    returnTo?: string; 
    paymentType?: 'ach' | 'card';
    successPage?: string;
    managePaymentPage?: string;
  } | undefined;
  ManageMyWallet: { 
    leaseId: number; 
    residentId?: number; 
    selectedMethodId?: string | number; 
    returnTo?: string;
    successPage?: string;
    addOrChangePaymentMethodPage?: string;
  } | undefined;
  TransactionHistory: { leaseId: number };
  LinkedAccounts: undefined;
  Marketplace: undefined;
  MarketplaceProfile: {
    action?: 'sold' | 'removed' | 'reposted';
    listingId?: string;
  } | undefined;
  MarketplaceItemDetail: {
    listing: MarketplaceListing;
  };
  SellerProfile: {
    sellerId: string;
  };
  MyListingItemDetail: {
    listing: MyListing;
  };
  SavedListings: {
    unsavedId?: string;
  } | undefined;
  SavedItemDetail: {
    listing: SavedListing;
  };
  MarketplaceInbox: undefined;
  InboxConversation: {
    conversationId: string;
  };
  MarketplaceFollowing: undefined;
  CommunityBoard: undefined;
  Surveys: { showBackLink?: boolean } | undefined;
  ThreadDetail: { threadId: string };
  CreateThread: undefined;
  EditThread: { threadId: string };
  MyThreads: undefined;
  SavedThreads: undefined;
  Resources: undefined;
  Messages: { initialSubject?: string; existingConversationId?: number } | undefined;
  Maintenance: undefined;
  Events: undefined;
  Wifi: undefined;
  Bulletins: undefined;
  Notifications: undefined;
  Account: { section?: 'notificationSettings' | 'accountInformation' | 'paymentMethods' | 'autopay' | 'resetPassword' } | undefined;
  FAQ: { url?: string } | undefined;
};

const ContentStack = createStackNavigator<DesktopMainParamList>();

const transparentTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: 'transparent',
  },
};

// Screen name → URL path mapping for browser history sync
const SCREEN_TO_PATH: Partial<Record<keyof DesktopMainParamList, string>> = {
  Home: '/home',
  Planner: '/planner',
  Payments: '/payments',
  Maintenance: '/maintenance',
  Messages: '/property-chat',
  Events: '/events',
  Marketplace: '/marketplace',
  CommunityBoard: '/newsfeed',
  Surveys: '/surveys',
  Notifications: '/notifications',
  Account: '/my-account',
  Resources: '/resources',
  LinkedAccounts: '/linked-accounts',
  FAQ: '/faq',
  Bulletins: '/bulletins',
  TransactionHistory: '/payments/history',
  MarketplaceProfile: '/marketplace/profile',
  MarketplaceItemDetail: '/marketplace/item',
  SellerProfile: '/marketplace/seller',
  MyListingItemDetail: '/marketplace/my-listing',
  SavedListings: '/marketplace/saved',
  SavedItemDetail: '/marketplace/saved-item',
  MarketplaceInbox: '/marketplace/inbox',
  InboxConversation: '/marketplace/conversation',
  MarketplaceFollowing: '/marketplace/following',
  ThreadDetail: '/newsfeed/thread',
  MyThreads: '/newsfeed/my-threads',
  SavedThreads: '/newsfeed/saved',
  Wifi: '/wifi',
};

// Reverse mapping: URL path → screen name (only main screens for initial URL resolution)
const PATH_TO_SCREEN: Record<string, keyof DesktopMainParamList> = Object.entries(SCREEN_TO_PATH).reduce(
  (acc, [screen, path]) => ({ ...acc, [path]: screen as keyof DesktopMainParamList }),
  {} as Record<string, keyof DesktopMainParamList>,
);

// Screens that appear in the sidebar (used for activeKey updates)
const SIDEBAR_SCREENS = new Set<string>([
  'Home', 'Planner', 'Payments', 'Maintenance', 'Messages', 'Events', 'Marketplace',
  'CommunityBoard', 'Surveys', 'Notifications', 'Account', 'Resources', 'LinkedAccounts', 'FAQ',
]);

// Modal screens should not push browser history entries
const MODAL_SCREENS = new Set<string>([
  'PaymentSummaryModal', 'ConfirmPaymentDetails', 'TransactionProcessing',
  'TransactionReceived', 'TransactionTimeout', 'AutoPayWizard',
  'AddOrChangePaymentMethod', 'ManageMyWallet', 'CreateThread', 'EditThread',
]);

// Map sub-screens to their parent sidebar item so the highlight stays correct
const SCREEN_TO_SIDEBAR_PARENT: Partial<Record<keyof DesktopMainParamList, keyof DesktopMainParamList>> = {
  TransactionHistory: 'Payments',
  PaymentSummaryModal: 'Payments',
  ConfirmPaymentDetails: 'Payments',
  TransactionProcessing: 'Payments',
  TransactionReceived: 'Payments',
  TransactionTimeout: 'Payments',
  AutoPayWizard: 'Payments',
  AddOrChangePaymentMethod: 'Payments',
  ManageMyWallet: 'Payments',
  MarketplaceProfile: 'Marketplace',
  MarketplaceItemDetail: 'Marketplace',
  SellerProfile: 'Marketplace',
  MyListingItemDetail: 'Marketplace',
  SavedListings: 'Marketplace',
  SavedItemDetail: 'Marketplace',
  MarketplaceInbox: 'Marketplace',
  InboxConversation: 'Marketplace',
  MarketplaceFollowing: 'Marketplace',
  ThreadDetail: 'CommunityBoard',
  CreateThread: 'CommunityBoard',
  EditThread: 'CommunityBoard',
  MyThreads: 'CommunityBoard',
  SavedThreads: 'CommunityBoard',
  Bulletins: 'Notifications',
  Wifi: 'Resources',
};

/** Resolve which sidebar item should be active for a given screen */
const getSidebarKeyForScreen = (screenName: string): keyof DesktopMainParamList | undefined => {
  if (SIDEBAR_SCREENS.has(screenName)) return screenName as keyof DesktopMainParamList;
  return SCREEN_TO_SIDEBAR_PARENT[screenName as keyof DesktopMainParamList];
};

// Web globals accessed only when Platform.OS === 'web'
declare const window: any;

/** Resolve screen name from current URL on initial load */
const getInitialRouteFromURL = (): keyof DesktopMainParamList => {
  if (Platform.OS !== 'web') return 'Home';
  const path = window.location.pathname;
  return PATH_TO_SCREEN[path] || 'Home';
};

export const DesktopNavigator: React.FC = () => {
  const { colors } = useThemeColors();
  const { layoutMode } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const contentNavRef = useNavigationContainerRef<DesktopMainParamList>();

  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  const initialRoute = getInitialRouteFromURL();
  const isProgrammaticNav = useRef(false);

  const [activeKey, setActiveKey] = useState<keyof DesktopMainParamList>(initialRoute);
  const [currentRouteName, setCurrentRouteName] = useState<keyof DesktopMainParamList>(initialRoute);

  // Set initial browser history state and URL
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const path = SCREEN_TO_PATH[initialRoute] || '/home';
    window.history.replaceState({ screen: initialRoute }, '', path);
  }, []);

  // Listen for browser back/forward buttons
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePopState = (event: any) => {
      if (!contentNavRef.isReady()) return;

      isProgrammaticNav.current = true;

      const screenName = event.state?.screen as keyof DesktopMainParamList | undefined;
      if (screenName) {
        contentNavRef.navigate(screenName as any);
      } else {
        // Fallback: resolve from URL
        const path = window.location.pathname;
        const resolved = PATH_TO_SCREEN[path];
        if (resolved) {
          contentNavRef.navigate(resolved as any);
        } else if (contentNavRef.canGoBack()) {
          contentNavRef.goBack();
        }
      }

      setTimeout(() => { isProgrammaticNav.current = false; }, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync sidebar highlight and browser URL on every navigation state change
  const handleNavigationStateChange = useCallback(() => {
    const route = contentNavRef.getCurrentRoute();
    if (!route) return;

    setCurrentRouteName(route.name as keyof DesktopMainParamList);

    // Always sync sidebar active state regardless of navigation source
    const sidebarKey = getSidebarKeyForScreen(route.name);
    if (sidebarKey) {
      setActiveKey(sidebarKey);
    }

    // Only push browser history for user-initiated navigations (not back/forward)
    if (Platform.OS === 'web' && !isProgrammaticNav.current && !MODAL_SCREENS.has(route.name)) {
      const path = SCREEN_TO_PATH[route.name as keyof DesktopMainParamList];
      if (path) {
        window.history.pushState({ screen: route.name }, '', path);
      }
    }
  }, []);

  const items: DesktopSidebarItem[] = useMemo(() => {
    const iconColor = (key: keyof DesktopMainParamList) => (activeKey === key ? colors.text.primary : colors.text.muted);
    
    // Helper to create sidebar item
    const createItem = (
      key: keyof DesktopMainParamList,
      title: string,
      iconKey: keyof typeof RESIDENTS_SVG_COMPONENTS,
      group: NavigationGroup
    ): DesktopSidebarItem => {
      const IconComponent = RESIDENTS_SVG_COMPONENTS[iconKey] as React.FC<{ width?: number; height?: number; color?: string }>;
      return {
        key,
        title,
        icon: <IconComponent width={24} height={24} color={iconColor(key)} />,
        onPress: () => { setActiveKey(key); contentNavRef.navigate(key as any); },
        isActive: activeKey === key,
        group,
      };
    };

    const allItems: (DesktopSidebarItem | false)[] = [
      // QUICK ACTIONS
      createItem('Home', 'Home', 'HOUSE', 'QUICK_ACTIONS'),
      createItem('Planner', 'Planner', 'CALENDAR', 'QUICK_ACTIONS'),
      shouldShowPayments && createItem('Payments', 'Payments', 'CREDIT_CARD', 'QUICK_ACTIONS'),
      createItem('Maintenance', 'Maintenance', 'WRENCH', 'QUICK_ACTIONS'),
      createItem('Messages', 'Property Chat', 'CHAT_ICON', 'QUICK_ACTIONS'),
      // COMMUNITY
      createItem('Events', 'Events', 'STAR', 'COMMUNITY'),
      createItem('Marketplace', 'Marketplace', 'SUPERMARKET', 'COMMUNITY'),
      createItem('CommunityBoard', 'Newsfeed', 'CHAT_ICON', 'COMMUNITY'),
      createItem('Surveys', 'Surveys', 'FAQ', 'COMMUNITY'),
      createItem('Notifications', 'Notifications', 'NOTIFICATION', 'COMMUNITY'),
      // MY ACCOUNT
      createItem('Account', 'Settings', 'USER', 'MY_ACCOUNT'),
      createItem('Resources', 'Resources', 'WIDGETS', 'MY_ACCOUNT'),
      createItem('LinkedAccounts', 'Linked Accounts', 'LINKED_ACCOUNTS', 'MY_ACCOUNT'),
      createItem('FAQ', 'FAQ', 'FAQ', 'MY_ACCOUNT'),
    ];

    return allItems.filter(Boolean) as DesktopSidebarItem[];
  }, [activeKey, shouldShowPayments]);

  const navigateToNotifications = () => contentNavRef.navigate('Notifications');
  const navigateToNotificationSettings = () => contentNavRef.navigate('Account', { section: 'notificationSettings' });
  const navigateToMessages = () => contentNavRef.navigate('Messages');

  // Map non-sidebar screens to display titles
  const SUB_SCREEN_TITLES: Partial<Record<string, string>> = {
    Wifi: 'WiFi',
    ThreadDetail: 'Thread',
    TransactionHistory: 'Transaction History',
  };

  const getHeaderTitle = () => {
    if (SUB_SCREEN_TITLES[currentRouteName]) {
      return SUB_SCREEN_TITLES[currentRouteName];
    }
    const item = items.find(i => i.key === activeKey);
    return item?.title || 'Resio';
  };

  const contentFrameWebStyle = Platform.OS === 'web' ? {
    backgroundImage: [
      `radial-gradient(ellipse 70% 35% at 15% 2%, rgba(71,115,254,0.35) 0%, rgba(36,58,127,0.18) 50%, transparent 100%)`,
      `radial-gradient(ellipse 70% 35% at 85% 2%, rgba(71,115,254,0.35) 0%, rgba(36,58,127,0.18) 50%, transparent 100%)`,
      `radial-gradient(ellipse 70% 30% at 50% 45%, rgba(190,94,200,0.28) 0%, rgba(190,94,200,0.12) 60%, transparent 100%)`,
      `radial-gradient(ellipse 65% 30% at 50% 100%, rgba(245,85,96,0.35) 0%, rgba(123,43,48,0.15) 50%, transparent 100%)`,
    ].join(', ') + `, linear-gradient(180deg, ${colors.bg.gradientStart} 0%, ${colors.bg.base} 100%)`,
  } as any : {};

  return (
    <View style={[styles.root, { backgroundColor: colors.bg.modal }]}>
      <DesktopHeader 
        title={getHeaderTitle()}
        onNavigateToBulletins={navigateToNotifications}
        onNavigateToNotificationSettings={navigateToNotificationSettings}
        onNavigateToChat={navigateToMessages}
      />
      <View style={styles.body}>
        {layoutMode === 'desktop' && (
          <DesktopSidebar items={items} />
        )}
        <View style={styles.contentOuter}> 
          <View style={[styles.contentFrame, { backgroundColor: colors.bg.gradientStart, borderColor: colors.card.border }, contentFrameWebStyle]}>
            <NavigationContainer independent ref={contentNavRef} theme={transparentTheme} onStateChange={handleNavigationStateChange}>
              <ContentStack.Navigator initialRouteName={initialRoute} screenOptions={{ headerShown: false, cardStyle: { backgroundColor: 'transparent' } }}>
                    <ContentStack.Screen name="Home" component={HomeScreen} />
                    <ContentStack.Screen name="Planner" component={PlannerScreen} />
                    {shouldShowPayments && (
                      <ContentStack.Screen name="Payments" component={PaymentsScreen} />
                    )}
                    <ContentStack.Screen 
                      name="PaymentSummaryModal" 
                      component={PaymentSummaryModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="ConfirmPaymentDetails" 
                      component={ConfirmPaymentDetailsModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    {/* <ContentStack.Screen name="PaymentWizard" component={PaymentWizardScreen as unknown as React.ComponentType<any>} /> */}
                    <ContentStack.Screen 
                      name="TransactionProcessing" 
                      component={TransactionProcessingScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="TransactionReceived" 
                      component={TransactionReceivedScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="TransactionTimeout" 
                      component={TransactionTimeoutScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="AutoPayWizard" 
                      component={AutoPayWizardScreen as unknown as React.ComponentType<any>} 
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="AddOrChangePaymentMethod" 
                      component={AddOrChangePaymentMethodModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen 
                      name="ManageMyWallet" 
                      component={ManageMyWalletModal as unknown as React.ComponentType<any>}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen name="TransactionHistory" component={TransactionHistoryScreen} />
                    <ContentStack.Screen name="LinkedAccounts" component={LinkedAccountsScreen} />
                    <ContentStack.Screen name="Marketplace" component={CommunityMarketplaceScreen} />
                    <ContentStack.Screen name="MarketplaceProfile" component={MarketplaceProfileScreen} />
                    <ContentStack.Screen name="MarketplaceItemDetail" component={MarketplaceItemDetailScreen} />
                    <ContentStack.Screen name="SellerProfile" component={SellerProfileScreen} />
                    <ContentStack.Screen name="MyListingItemDetail" component={MyListingItemDetailScreen} />
                    <ContentStack.Screen name="SavedListings" component={SavedListingsScreen} />
                    <ContentStack.Screen name="SavedItemDetail" component={SavedItemDetailScreen} />
                    <ContentStack.Screen name="MarketplaceInbox" component={MarketplaceInboxScreen} />
                    <ContentStack.Screen name="InboxConversation" component={InboxConversationScreen} />
                    <ContentStack.Screen name="MarketplaceFollowing" component={MarketplaceFollowingScreen} />
                    <ContentStack.Screen name="CommunityBoard" component={CommunityBoardScreen} />
                    <ContentStack.Screen name="Surveys" component={SurveysListScreen} />
                    <ContentStack.Screen name="ThreadDetail" component={ThreadDetailScreen} />
                    <ContentStack.Screen
                      name="CreateThread"
                      component={CreateThreadScreen}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen
                      name="EditThread"
                      component={EditThreadScreen}
                      options={{
                        headerShown: false,
                        presentation: 'transparentModal',
                        cardStyle: { backgroundColor: 'transparent' },
                        animationEnabled: false,
                      }}
                    />
                    <ContentStack.Screen name="MyThreads" component={MyThreadsScreen} />
                    <ContentStack.Screen name="SavedThreads" component={SavedThreadsScreen} />
                    <ContentStack.Screen name="Resources" component={DesktopCommunityResourcesView} />
                    <ContentStack.Screen name="Messages" component={MessagesScreen} />
                    {/* TODO: REVISIT FEATURES */}
                    <ContentStack.Screen name="Maintenance" component={MaintenanceScreen} />
                    <ContentStack.Screen name="Events" component={DesktopEventsScreen as unknown as React.ComponentType<any>} />
                    <ContentStack.Screen name="Wifi" component={require('@/screens/residents/wifi/desktop/DesktopWifiScreen').default} />
                    <ContentStack.Screen name="Bulletins" component={DesktopBulletinsScreen} />
                    <ContentStack.Screen name="Notifications" component={require('@/screens/residents/notifications/desktop/DesktopNotificationsScreen').default} />
                    <ContentStack.Screen name="Account" component={DesktopAccountScreen as unknown as React.ComponentType<any>} />
                    <ContentStack.Screen name="FAQ" component={DesktopFAQScreen} />
              </ContentStack.Navigator>
            </NavigationContainer>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  body: {
    flex: 1,
    flexDirection: 'row',
    paddingLeft: 24,
    paddingRight: 24,
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
  },
  contentOuter: {
    flex: 1,
    paddingRight: 24,
    paddingLeft: 24,
    paddingTop: 8,
  },
  contentFrame: {
    flex: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    borderBottomWidth: 0,
    overflow: 'hidden',
  },
});


